import axios from 'axios';
import t from 'typy';
import helpers from '@js/helpers';
import { configureAxiosCsrf } from '@js/csrf';

configureAxiosCsrf(axios);

// Run function after any response gets received
// GTM support
axios.interceptors.response.use(response => {
    try {
        const events = t(response, 'data.meta._analytics_events').safeArray;
		setTimeout(function () {
			for (let i = 0; i < events.length; i++) {
				setDataLayer(events[i]);
			}
			performPostBacks(events);
		}, 3000); // anticipate a page redirect
    } catch (error) {
        console.error(error);
    }

    return response;
});

axios.interceptors.request.use(config => {
    config.headers = config.headers || {};
    config.headers['current-page'] = (window.location && window.location.pathname) || null;
    return config;
});

export default {
	setVshredHeaders() {
		axios.defaults.headers.common.source = 'vshred';
	},

	/*
		------------------------------------------------------------
			Product
		------------------------------------------------------------
	*/

    /*
        (getShopOffers, getFeaturedOffers) might get kind of messy
        Allow parameters to be passed into method in future that are parsed
        and attatched to request url
    */

    /**
     * Gets all shop offers
     * @param  {String} category - The product category filter
     */
    async getShopOffers(category) {
        // console.log(`GET shop offers`);
        try {
            const params = {'filter[isShop]': 1};
            if (category) params['filter[inCategory]'] = category;
            const response = await axios.get('/api/offer', {params});
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    /**
     * Gets all featured offers
     */
    async getFeaturedOffers() {
        // console.log(`GET featured offers`);
        try {
            const response = await axios.get('/api/offer/?filter[isFeatured]=1');
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    /**
     * Gets a specific offer
     * @param  {String} sku - The SKU of the offer to retrieve
     */
    async getOffer(sku) {
        // console.log(`GET offer ${sku}`);
        try {
            const response = await axios.get(`/api/offer/${sku}`);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    /**
     * Gets a specific product
     * @param  {String} sku - The SKU of the product to retrieve
     */
    async getProduct(sku) {
        // console.log(`GET product ${sku}`);
        try {
            const response = await axios.get(`/api/product/${sku}`);
            return response.data;
        } catch (error) {
            throw error;
        }
    },


    /*
        ------------------------------------------------------------
            User
        ------------------------------------------------------------
    */

    /**
     * Gets the user modal
     */
    async getUser() {
        // console.log(`GET user`);
        try {
            const response = await axios.get('/api/user');
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    /**
     * Updates the user modal
     * @param  {Object} user - An object with properties to update on the user modal
     */
    async updateUser(user) {
        // console.log(`PATCH user`);
        try {
            const response = await axios.patch('/api/user', user);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    /**
     * Attempts to log in
     * @param  {Object}  data - An object with login data
     * @param  {String}  data.email - The username/email for the account
     * @param  {String}  data.password - The password for the account
     * @param  {Boolean} data.remember - Whether or not to remember this user over multiple sessions
     */
    async login(data) {
        // console.log(`POST login`);
        try {
            const response = await axios.post('/login', data);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    /**
     * Attempts to log out the current logged in user
     */
    async logout() {
        // console.log(`POST logout`);
        try {
            const response = await axios.post('/logout');
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    /**
	 * Check if a given email is already linked to an existing user
	 * @param  {String} email - The email of the user
	 */
	async checkEmail(email) {
		// console.log(`POST check email ${email}`);
		try {
			const response = await axios.post('/api/auth/check-email', { email });
			return response;
		} catch(error) {
			throw error;
		}
	},

    /**
     * Get the current payment method for the logged in user
     */
    async getPaymentMethod() {
        // console.log(`GET payment method`);
        try {
            const response = await axios.get('/api/user/payment-method');
            return response.data;
        } catch(error) {
            throw error;
        }
    },

    /**
     * Attempts to send a reset password link to the specified email
     * @param  {Object}  data - An object with user data
     * @param  {String}  data.email - The username/email for the account
     */
    async sendForgotPasswordLink(data) {
        // console.log(`POST send forgot password link`);
        try {
            const response = await axios.post('/password/email', data);
            return response.data;
        } catch(error) {
            // console.log(error)
            throw error;
        }
    },

    /**
     * Attempts to reset the password of a specified user
     * @param  {Object}  data - An object with user data
     * @param  {String}  data.token - The reset request token
     * @param  {String}  data.email - The username/email for the account
     * @param  {String}  data.password - The new password for the account
     * @param  {String}  data.password_confirmation - New password confirmation
     */
    async resetPassword(data) {
        // console.log(`POST reset password`);
        try {
            const response = await axios.post('/password/reset', data);
            return response.data;
        } catch(error) {
            throw error;
        }
    },

    async checkCDPQuestionnaire(email) {
        try {
            const response = await axios.post('/api/user/subscription/internal/check-cdp-questionnaire', {
                email: email,
            });

            return response.data;
        } catch (error) {
            throw error;
        }
    },

    async getSubscriptionDeferrals(subscriptionId) {
        try {
            const response = await axios.get(`/api/user/subscription/${subscriptionId}/deferrals`);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    /*
        ------------------------------------------------------------
            Cart
        ------------------------------------------------------------
    */

    /**
     * Gets all the offers in the cart
     */
    async getCart(params = {}) {
        // console.log(`GET cart`);
        try {
            const recoveredBy = helpers.getHrefParameters().rb;
            recoveredBy ? params.rb = recoveredBy : null;

            const response = await axios.get(`/api/cart`, {params});
            return response.data;
        } catch (error) {
            throw error;
        }
    },

	/**
	 * Fetch Cart using Discount 'coupon_code'
	 */
	async getCartByDiscount(discount = '') {
		try {
			const response = await axios.get(`/api/order/${discount}`);

			return response.data;
		} catch (error) {
			throw error;
		}
	},

    /**
     * Adds an offer to the cart
     * @param {Object} data
     * @param {Number} data.id       - The id of the offer to add
     * @param {Number} data.quantity - The quantity of the offer to add
     */
    async addOfferToCart(data) {
        if (!data) throw 'addOfferToCart API method requires a request object parameter to be passed!';
        // console.log(`POST add offer ${data.id} to cart`);
        try {
            const requestData = {
                offer_id: data.id,
                quantity: data.quantity || 1
            };
            return await axios.post('/api/cart/offer', requestData);
        } catch (error) {
            throw error;
        }
    },

    /**
     * Removes an offer from the cart
     * @param  {Number} id - The id of the offer to remove from the cart
     */
    async removeOfferFromCart(id) {
        // console.log(`DELETE remove offer ${id} from cart`);
        try {
            const response = await axios.delete(`/api/cart/offer/${id}`);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    /**
     * Updates the quantity of an offer in the cart
     * @param  {Object} data
     * @param  {Object} data.id       - The id of the offer
     * @param  {Object} data.quantity - The quantity of the offer
     */
    async updateCartOfferQuantity(data) {
        if (!data) throw 'updateCartOfferQuantity API method requires a request object parameter to be passed!';
        // console.log(`PATCH update offer ${data.id} quantity in cart`);
        try {
            const response = await axios.patch(`/api/cart/offer/${data.id}`, {quantity: data.quantity});
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    /**
     * Gets the shipping options for the current cart
     * @param  {Object} params - The parameters to pass with the request
     */
    async getShippingCategories(params = {}) {
        // console.log(`GET shipping categories`);
        try {
            const response = await axios.get(`/api/cart/shipping-categories`, {params});
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    /**
     * Gets the shipping tracking by order ID
     */
    async getShipmentTracking(id) {
        console.log(`GET shipments tracking by order id ${id}`);
        try {
            const response = await axios.get(`/api/user/order/${id}/shipments/tracking`);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    /**
     * Sets the shipping option to use for the current cart
     */
    async setShippingCategory(id) {
        // console.log(`POST shipping category with id ${id}`);
        try {
            const response = await axios.post(`/api/cart/shipping-categories/${id}`);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    /**
     * Apply a coupon to the cart
     * @param  {String} coupon - The coupon code to apply
     */
    async addCouponToCart(coupon) {
        // console.log(`POST cart coupon ${coupon}`);
        try {
            const response = await axios.post(`/api/cart/discount/${coupon}`);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    /**
     * Remove a coupon from the cart
     * @param  {String} coupon - The coupon code to remove
     */
    async removeCouponFromCart(coupon) {
        // console.log(`DELETE cart coupon ${coupon}`);
        try {
            const response = await axios.delete(`/api/cart/discount${coupon}`);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    /**
     * Update the user's cart
     * @param  {Object} data - The data to update in the cart
     */
    async updateCart(data) {
        // console.log(`POST cart update`);
        try {
            const response = await axios.patch(`/api/cart`, data);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    /**
     * Charge the user and finalize the order
     * @param  {Object} data - The data necessary to pay the cart
     */
    async payCart(data) {
		// console.log(`POST cart pay`);
		const response = await axios.post(`/api/cart/pay`, this.getPayCartPayload(data));
		return response.data;
	},

	/**
	 * Submit Adyen 3DS2 fingerprint or challenge details after handleAction().
	 * @param  {Object} details - Adyen Web Component details payload
	 */
	async submitAdyenPaymentDetails(details) {
		const response = await axios.post('/api/cart/pay/adyen-details', {
			details: details || {},
		});

		return response.data;
	},

    getShopperReturnContext() {
        if (typeof window === 'undefined' || !window.location?.href) {
            return null;
        }

        return {
            returnUrl: window.location.href,
            origin: window.location.origin,
        };
    },

    getPayCartPayload(data) {
        try {
            const payload = { ...data };
            const shopperContext = this.getShopperReturnContext();

            if (shopperContext && payload.payment) {
                payload.payment = {
                    ...payload.payment,
                    details: {
                        ...(payload.payment.details || {}),
                        ...shopperContext,
                    },
                };
            }

            const queryString = window.location.search;
            const urlParams = new URLSearchParams(queryString);
            const nonceOverride = urlParams.get('test_nonce_override');

            if (nonceOverride) {
                payload.nonceOverride = nonceOverride;
            }

            return payload;
        } catch (e) {
            console.error(e);
            return data;
        }
    },

	async savePayment(data) {
		try {
			const response = await axios.patch(`/api/cart/payment-token`, data);
			return response.data;
		} catch (error) {
			throw error;
		}
	},

	async preAuth(data) {
		try {
			const response = await axios.post(`/api/cart/pre-auth`, data);
			return response.data;
		} catch (error) {
		}
	},


	/*
		------------------------------------------------------------
			Orders/Subscriptions
		------------------------------------------------------------
	*/

    /**
     * Gets the current user's orders
     * @param  {Object} params - The parameters to pass with the request
     */
    async getOrders(params = {}) {
        // console.log(`GET orders`);
        try {
            const response = await axios.get('/api/user/order', {params});
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    /**
     * Gets the current user's orders in a lightweight format
     * @param  {Object} params - The parameters to pass with the request
     */
    async getOrdersList(params = {}) {
        try {
            const response = await axios.get('/api/user/order/list', {
                params: {
                    ...params,
                    lightweight: true
                }
            });
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    /**
     * Gets a specific order
     * @param  {String|Number} id - The ID of the order to retrieve
     * @param  {Object} params - Optional parameters for the request
     */
    async getOrderById(id, params = {}) {
        try {
            // Convert array includes to proper format
            if (params.include && Array.isArray(params.include)) {
                params.include = params.include.join(',');
            }
            const response = await axios.get(`/api/user/order/${id}`, { params });
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    /**
     * Add an order to the user (for an upsell)
     * @param  {Object} order        - The order object
     * @param  {Array}  order.offers - A list of offers to add to the order
     */
    async acceptUpsell(order) {
        // console.log(`POST order (upsell)`);
        try {
            const response = await axios.post('/api/user/order', order);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    /**
     * Reject an upsell
     */
    async rejectUpsell() {
        // console.log(`POST order optout (upsell)`);
        try {
            const response = await axios.post('/api/user/order/optout');
            return response;
        } catch (error) {
            throw error;
        }
    },

    /**
     * Gets the current user's subscriptions (past and present)
     * @param  {Object} params - The parameters to pass with the request
     */
    async getSubscriptions(params = {}) {
        // console.log(`GET subscriptions`);
        try {
            const response = await axios.get('/api/user/subscription', {params});
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    /**
     * Gets the current user's subscriptions in a lightweight format for list views
     * @param  {Object} params - The parameters to pass with the request
     */
    async getSubscriptionsList(params = {}) {
        try {
            const response = await axios.get('/api/user/subscription/list', { params });
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    /**
     * Gets a subscription order by its id
     * @param  {Number} id - The id of the subscription
     */
    async getSubscriptionById(id, params = {}) {
        try {
            // Convert array includes to proper format
            if (params.include && Array.isArray(params.include)) {
                params.include = params.include.join(',');
            }
            const response = await axios.get(`/api/user/subscription/${id}`, { params });
            return response.data;
        } catch(error) {
            throw error;
        }
    },

    /**
     * Cancels a subscription order by its id
     * @param  {Number} id - The id of the subscription
     */
    async cancelSubscription(id) {
        // console.log(`DELETE subscription with id ${id}`);
        try {
            const url = `/api/user/subscription/${id}`;
            const response = await axios.delete(url);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    /**
     * Skips a shipment for a subscription order by its id
     * @param  {Number} id - The id of the subscription
     */
    async skipMonth(id) {
        try {
            const url = `/api/user/subscription/${id}/skip`;
            const response = await axios.post(url);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

	/**
	 * Updates the user's payment method
	 * @param  {Object}  data - An object with payment method data
	 * @param  {String}  data.token - The token to use to update the payment method
	 * @param  {String}  data.gateway - The gateway of the token provided
	 */
	async updateDefaultPaymentMethod(data) {
		// console.log(`POST payment-method`);
		try {
			const response = await axios.post(`/api/user/payment-method`, data);
			return response.data;
		} catch (error) {
			throw error;
		}
	},

	/**
	 * Refund the user's order offers
	 * @param {Array}  		 orderOfferIds - The order offer ids to refund
	 * @param {Integer|null} orderId - The order for full refund
	 * @param {Array}   	 cancelSubscriptionIds - Order offer ids to cancel subscriptions
	 * @param {String}  	 refundReason - The reason for the refund
	 * @param {Boolean} 	 fulfilled - Has the order synced to fulfillment
	 * @param {Boolean} 	 fullRefund - Eligible for refund
	 */
	async selfRefund(orderOfferIds, orderId,  cancelSubscriptionIds, refundReason, fulfilled, fullRefund) {
		try {
			const response = await axios.post(`/api/self-refund`, {
				order_offer_ids: orderOfferIds,
				order_id: orderId,
				cancel_subscription_order_offer_ids: cancelSubscriptionIds,
				refund_reason: refundReason,
				fulfilled: fulfilled || false,
				full_refund: fullRefund || false
			});
			return response.data;
		} catch (error) {
			throw error;
		}
	},

	/**
	 * Refund/Return the user's order offers
	 * @param orderOffers
	 * @param returnReason
	 * @returns {Promise<*>}
	 */
	async selfReturn(orderOffers, returnReason) {
		try {
			const response = await axios.post(`/api/self-return`, {
				order_offers: orderOffers,
				return_reason: returnReason
			});
			return response.data;
		} catch (error) {
			throw error;
		}
	},

	/**
	 * Deflect return of the user's order offers
	 * @param orderOfferIds
	 * @param refundReason
	 * @returns {Promise<*>}
	 */
	async selfDeflect(orderOfferIds, refundReason) {
		try {
			const response = await axios.post(`/api/self-deflect`, {
				order_offer_ids: orderOfferIds,
				refund_reason: refundReason
			});
			return response.data;
		} catch (error) {
			throw error;
		}
	},


    /*
        ------------------------------------------------------------
            Address
        ------------------------------------------------------------
    */

    /**
     * Gets an address of the current user by its id
     * @param  {Number} id - The id of the address
     */
    async getAddress(id) {
        // console.log(`GET address with id ${id}`);
        try {
            const response = await axios.get(`/api/user/address/${id}`);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    /**
     * Gets all the user's addresses
     */
    async getAddresses() {
        // console.log(`GET addresses`);
        try {
            const response = await axios.get('/api/user/address');
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    /**
     * Updates one of the user's addresses
     * @param  {Number} id      - The id of the address to update
     * @param  {Object} address - An object with address data
     */
    async updateAddress(id, address) {
        // console.log(`PATCH address with id ${id}`);
        try {
            const response = await axios.patch(`/api/user/address/${id}`, address);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    /**
     * Verifies address info in checkout
     * @param  {Object} params - An object with address data
     */
     async validateAddress(params = {}) {
        const postalCodePatterns = {
            USA: /^\d{5}(?:[-]?\d{4})?$/,
            CAN: /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z][ -]?\d[ABCEGHJ-NPRSTV-Z]\d$/i,
            GBR: /^[A-Z]{1,2}[0-9][A-Z0-9]? ?[0-9][A-Z]{2}$/i,
            // Default: up to 9 alphanumeric, optionally separated by a single dash, space, or dot (not at start/end, not consecutive, not trailing)
            DEFAULT: /^(?=.{1,11}$)(?=[A-Za-z0-9]{1,9}$|[A-Za-z0-9]{1,9}[\s\-.][A-Za-z0-9]{1,9}$)[A-Za-z0-9]+([\s\-.][A-Za-z0-9]+)?$/,
        };

        if (!params.postal_code || !params.country) {
            return {
                data: {
                    is_valid: false,
                    valid_format: false,
                    message: 'Postal code and country are required',
                },
            };
        }

        const countryCode = (params.country || '').toUpperCase();
        const pattern = postalCodePatterns[countryCode] || postalCodePatterns.DEFAULT;
        const isValid = pattern.test(params.postal_code);
        if (!isValid) {
            return {
                data: {
                    is_valid: false,
                    valid_format: false,
                    message: 'Invalid postal code format',
                },
            };
        }

        try {
            const response = await axios.get(`/api/location-check/valid-postal-code`, { params });
            return { data: response.data.data || response.data };
        } catch (error) {
            return {
                data: {
                    is_valid: false,
                    error: error?.response?.data?.message || error.message || 'Failed to validate address',
                },
            };
        }
    },

    /**
     * Retrieves address details based on provided zip code
     * @param  {String} postalCode - a string containing the zip code
	 * @param  {String} country    - a string containing the country code
     */
    async lookupAddressFromZipCode(postalCode, country) {
        // console.log(postalCode);
        try {
            const response = await axios.get(`/api/location-check/lookup-postal-code`, {params: {postal_code: postalCode, country: country}});
            return response.data;
        } catch (error) {
            // console.log(error);
            // throw error;
        }
     },


    /*
        ------------------------------------------------------------
            Tracking
        ------------------------------------------------------------
    */

    /**
     * Send a page click event to the tracking service
     * @param  {String} uri  - The uri of the page to track
     */
    async trackPageClick(uri) {
        // console.log(`Track a click on ${uri}`);
        try {
            const response = await axios.post(window.ECOM.PT_URL, {page: uri});
            return response.data;
        } catch (error) {
            throw error;
        }
    },

	/*
        ------------------------------------------------------------
            Refundable Reasons
        ------------------------------------------------------------
    */
	async getRefundReasons() {
		const response = await axios.get('/api/refund-reason');
		return response.data;
	},

	/*
        ------------------------------------------------------------
            Adyen Endpoints
        ------------------------------------------------------------
    */
	async paymentMethods() {
		const response = await axios.get('/api/adyen/payment/methods');
		return response.data;
	},

	async paymentSession(params = {}) {
		const response = await axios.get('/api/adyen/payment/session', {params});
		return response.data;
	},

	async paymentToken() {
		const response = await axios.get('/api/adyen/payment-token');
		return response.data;
	}

};
